export interface RetryOptions {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
  jitter?: boolean;
  retryCondition?: (error: Error, attempt: number) => boolean;
  onRetry?: (error: Error, attempt: number) => void;
}

export const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxAttempts: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffFactor: 2,
  jitter: true,
};

export class RetryManager {
  static async withRetry<T>(
    operation: () => Promise<T>,
    options: Partial<RetryOptions> = {}
  ): Promise<T> {
    const config = { ...DEFAULT_RETRY_OPTIONS, ...options };
    let lastError: Error;
    const startTime = Date.now();

    for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
      try {
        const result = await operation();
        
        // Log successful retry if it wasn't the first attempt
        if (attempt > 1) {
          console.info(`✅ Operation succeeded on attempt ${attempt} after ${Date.now() - startTime}ms`);
        }
        
        return result;
      } catch (error) {
        lastError = error as Error;
        
        // Check if we should retry this error
        if (config.retryCondition && !config.retryCondition(lastError, attempt)) {
          console.warn(`❌ Retry condition failed for attempt ${attempt}, not retrying:`, error);
          throw lastError;
        }

        // Don't retry if this is the last attempt
        if (attempt === config.maxAttempts) {
          console.error(`❌ All ${config.maxAttempts} attempts failed after ${Date.now() - startTime}ms:`, lastError);
          throw lastError;
        }

        // Check if error is retryable
        if (!this.shouldRetry(lastError)) {
          console.warn(`❌ Error is not retryable, stopping after attempt ${attempt}:`, error);
          throw lastError;
        }

        // Calculate delay with exponential backoff and optional jitter
        let delay = Math.min(
          config.baseDelay * Math.pow(config.backoffFactor, attempt - 1),
          config.maxDelay
        );

        // Add jitter to prevent thundering herd
        if (config.jitter) {
          delay = delay * (0.5 + Math.random() * 0.5);
        }

        console.warn(`⚠️ Attempt ${attempt}/${config.maxAttempts} failed, retrying in ${Math.round(delay)}ms:`, error);
        
        // Call retry callback if provided
        if (config.onRetry) {
          config.onRetry(lastError, attempt);
        }

        await this.delay(delay);
      }
    }

    throw lastError!;
  }

  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static shouldRetry(error: Error): boolean {
    // Don't retry on client errors (4xx), but retry on server errors (5xx) and network errors
    if (error.message.includes('400') || 
        error.message.includes('401') || 
        error.message.includes('403') || 
        error.message.includes('404')) {
      return false;
    }
    
    // Don't retry on API limit exceeded (429) - handle separately
    if (error.message.includes('429')) {
      return false;
    }
    
    // Retry on network errors, timeouts, and server errors
    return (
      error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('timeout') ||
      error.message.includes('500') ||
      error.message.includes('502') ||
      error.message.includes('503') ||
      error.message.includes('504') ||
      error.name === 'AbortError' ||
      error.name === 'TypeError' ||
      error.name === 'NetworkError'
    );
  }

  static createRetryCondition(
    maxNetworkRetries: number = 3,
    maxServerRetries: number = 2
  ): (error: Error, attempt: number) => boolean {
    return (error: Error, attempt: number): boolean => {
      const isNetworkError = error.name === 'TypeError' || 
                            error.name === 'AbortError' ||
                            error.message.includes('fetch') ||
                            error.message.includes('network');
      
      const isServerError = error.message.includes('500') ||
                           error.message.includes('502') ||
                           error.message.includes('503') ||
                           error.message.includes('504');

      if (isNetworkError && attempt <= maxNetworkRetries) {
        return true;
      }

      if (isServerError && attempt <= maxServerRetries) {
        return true;
      }

      return false;
    };
  }

  static createProgressiveRetryOptions(context: 'location' | 'weather'): RetryOptions {
    if (context === 'location') {
      return {
        maxAttempts: 2,
        baseDelay: 500,
        maxDelay: 2000,
        backoffFactor: 2,
        jitter: true,
        retryCondition: this.createRetryCondition(2, 1)
      };
    }

    // Weather API gets more retries since it's more critical
    return {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 8000,
      backoffFactor: 2,
      jitter: true,
      retryCondition: this.createRetryCondition(3, 2)
    };
  }
}